import React, { useMemo, useState } from "react";
import { Box, Flex, Grid, Image, Text, Tooltip } from "@chakra-ui/react";
import SVG from "react-inlinesvg";
import DashboardCard from "@/features/dashboard/components/dashboard-card";
import SkuPriceChart from "@/features/sku-dashboard/components/SkuPriceChart";
import { SkuInfo, SkuProduct } from "@/features/sku-dashboard/hooks/use-get-sku";
import useRefreshKreamSaleHistory from "@/features/sku-dashboard/hooks/use-refresh-kream-sale-history";
import useGetRefreshSaleHistoryStatus from "@/features/sku-dashboard/hooks/use-get-refresh-sale-history-status";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useIdentity from "@/features/ui/hooks/use-identity";
import useCreateOrderSheetSet from "@/features/line-sheet-sets/hooks/use-create-order-sheet-set";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  EstimatedSupplyBreakdown,
  getEstimatedSupplyBreakdown,
} from "@/features/order-sheet-sets/utils/order-sheet-market-product";
import EstimatedSupplyTooltip from "@/features/order-sheet-sets/order-sheet-estimated-supply-tooltip";
import { getPathPickMargin, PathPickMargin } from "@/features/pathpick/utils/market";

interface Props {
  data: SkuInfo;
  onRefresh?: () => void | Promise<void>;
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, "0")}.${String(d.getDate()).padStart(2, "0")}`;
}

function formatUpdatedAt(dateStr: string) {
  const d = new Date(dateStr);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
}

const formatKrw = (value: number) => `₩${Math.round(value).toLocaleString()}`;
const formatSigned = (value: number) =>
  `${value >= 0 ? "+" : "-"}₩${Math.abs(Math.round(value)).toLocaleString()}`;
const marginColor = (value: number | null | undefined) =>
  value == null ? "#8F8F8C" : value >= 0 ? "#2D9A5F" : "#E53E3E";

// 오더룰(컨디션)이 적용 완료(APPLIED)가 아닌 세트는 전부 — 공급가가 컨디션 반영 전 값일 수 있어 확인이 필요하다.
// NO_ORDER_RULE 도 포함한다: 오더룰 행이 빈 내용이면 자동으로 '없음' 처리되는데(세트 29048, 전 상품 MARKUP 0)
// 실제로는 컨디션이 안 들어간 경우가 섞여 있어 바이어 입장에선 확인 대상이다.
const needsOrderRuleCheck = (product: SkuProduct) =>
  product.lineSheetSet.orderRuleStatus !== "APPLIED";

/** 상품 한 줄의 계산 결과 — 예상 매입가(주문서 식)와 크림·포이즌 기준 마진. */
interface ProductEstimate {
  breakdown: EstimatedSupplyBreakdown | null;
  kream: PathPickMargin;
  poizon: PathPickMargin;
}

function MarginText({
  margin,
  isUnavailable,
  unavailableHint,
  textStyle = "body-sm-medium",
}: {
  margin: PathPickMargin;
  isUnavailable: boolean;
  unavailableHint: string;
  textStyle?: string;
}) {
  if (isUnavailable) {
    return (
      <Tooltip label={unavailableHint} placement={"top"} hasArrow={true} openDelay={150}>
        <Text textStyle={textStyle} color={"#8F8F8C"} whiteSpace={"nowrap"} cursor={"help"}>
          -
        </Text>
      </Tooltip>
    );
  }
  if (margin.expectedMargin == null) {
    return (
      <Text textStyle={textStyle} color={"#8F8F8C"} whiteSpace={"nowrap"}>
        -
      </Text>
    );
  }
  return (
    <Text textStyle={textStyle} color={marginColor(margin.expectedMargin)} whiteSpace={"nowrap"}>
      {formatSigned(margin.expectedMargin)}
      {margin.marginRate != null && (
        <Text as={"span"} textStyle={"caption-regular"} ml={"4px"}>
          {`(${margin.marginRate >= 0 ? "+" : ""}${Math.round(margin.marginRate)}%)`}
        </Text>
      )}
    </Text>
  );
}

/** 그래프 위 통계 칸 — 라벨(한 줄)·값·보조설명(한 줄)의 높이를 고정해 칸끼리 줄이 맞는다. */
function Stat({
  label,
  sub,
  children,
}: {
  label: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <Flex
      flexDirection={"column"}
      gap={"2px"}
      minW={0}
      padding={"8px 10px"}
      borderRadius={"6px"}
      background={"#F7F7F5"}
    >
      <Text textStyle={"caption-regular"} color={"#8F8F8C"} whiteSpace={"nowrap"} overflow={"hidden"} textOverflow={"ellipsis"}>
        {label}
      </Text>
      <Box minH={"36px"} display={"flex"} alignItems={"center"}>
        {children}
      </Box>
      {sub && (
        <Text textStyle={"caption-regular"} color={"#B5B5B2"} whiteSpace={"nowrap"} overflow={"hidden"} textOverflow={"ellipsis"}>
          {sub}
        </Text>
      )}
    </Flex>
  );
}

function ProductCard({
  product,
  estimate,
  isHovered,
  isKreamMarginUnavailable,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  product: SkuProduct;
  estimate: ProductEstimate;
  isHovered: boolean;
  isKreamMarginUnavailable: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}) {
  const { t, tTitle } = useI18nHelper();
  const orderRuleCheck = needsOrderRuleCheck(product);
  return (
    <Box
      borderRadius={"6px"}
      boxShadow={isHovered ? "0 0 0 1.5px #48B5FE" : "0 0 0 0.5px #D4D4D1"}
      background={isHovered ? "#F0F9FF" : "#FFFFFF"}
      padding={"14px 10px"}
      cursor={"pointer"}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      transition={"all 0.15s"}
    >
      <Flex gap={"16px"} alignItems={"center"} overflow={"hidden"}>
        <Flex flexDirection={"column"} flex={1} minW={0} gap={"2px"}>
          <Flex alignItems={"center"} gap={"6px"} minW={0}>
            <Text
              textStyle={"body-sm-medium"}
              color={"#1A1A1A"}
              whiteSpace={"nowrap"}
              overflow={"hidden"}
              textOverflow={"ellipsis"}
            >
              {product.lineSheetSet.boutiqueName}
            </Text>
            {orderRuleCheck && (
              <Tooltip label={t("order_rule_check_required_hint")} placement={"top"} hasArrow={true} openDelay={150}>
                <Box
                  flexShrink={0}
                  border={"1px solid #F6AD55"}
                  background={"#FFFAF0"}
                  color={"#C05621"}
                  borderRadius={"2px"}
                  paddingX={"6px"}
                  fontSize={"11px"}
                  fontWeight={600}
                  lineHeight={"18px"}
                  whiteSpace={"nowrap"}
                  cursor={"help"}
                >
                  {t("order_rule_check_required")}
                </Box>
              </Tooltip>
            )}
          </Flex>
          <Text
            textStyle={"body-sm-regular"}
            color={"#6F6F6C"}
            whiteSpace={"nowrap"}
            overflow={"hidden"}
            textOverflow={"ellipsis"}
          >
            #{product.lineSheetSet.id} {product.lineSheetSet.name}
          </Text>
          {formatDate(product.lineSheetSet.closedAt) && (
            <Text textStyle={"caption-regular"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
              ~{formatDate(product.lineSheetSet.closedAt)}
            </Text>
          )}
        </Flex>
        <Flex flexDirection={"column"} width={"72px"} flexShrink={0} alignItems={"flex-end"} gap={"2px"}>
          <Text textStyle={"body-sm-medium"} color={"#6B46C1"} fontWeight={"600"} whiteSpace={"nowrap"}>
            {product.priceAdjustedBy && (
              <Text as={"span"} color={"#8F8F8C"} fontWeight={"400"} mr={"4px"}>
                ({product.priceAdjustedBy.by === "DISCOUNT"
                  ? `R-${Math.abs(product.priceAdjustedBy.value)}`
                  : product.priceAdjustedBy.by === "MARKUP"
                    ? `C${product.priceAdjustedBy.value >= 0 ? "+" : ""}${product.priceAdjustedBy.value}`
                    : product.priceAdjustedBy.value})
              </Text>
            )}
            {product.supplyPrice ? `€${product.supplyPrice.toLocaleString()}` : "-"}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
            {product.costPrice ? `€${product.costPrice.toLocaleString()}` : "-"}
          </Text>
          <Text textStyle={"body-sm-medium"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
            {product.retailPrice ? `€${product.retailPrice.toLocaleString()}` : "-"}
          </Text>
        </Flex>
        {/* 예상 매입가 — 주문서와 같은 식. 값에 올리면 계산식(공급가 × 부대비용 × 환율)이 보인다 */}
        <Box width={"120px"} flexShrink={0} textAlign={"right"}>
          <EstimatedSupplyTooltip breakdown={estimate.breakdown}>
            <Text
              textStyle={"body-sm-medium"}
              color={"#1A1A1A"}
              whiteSpace={"nowrap"}
              cursor={estimate.breakdown ? "help" : undefined}
              textDecoration={estimate.breakdown ? "underline dotted #B5B5B2" : undefined}
              textUnderlineOffset={"3px"}
            >
              {estimate.breakdown ? formatKrw(estimate.breakdown.value) : "-"}
            </Text>
          </EstimatedSupplyTooltip>
        </Box>
        <Flex flexDirection={"column"} width={"168px"} flexShrink={0} alignItems={"flex-end"} gap={"1px"}>
          <Flex gap={"4px"} alignItems={"baseline"}>
            <Text textStyle={"caption-regular"} color={"#8F8F8C"}>
              {tTitle("market_source.kream")}
            </Text>
            <MarginText
              margin={estimate.kream}
              isUnavailable={isKreamMarginUnavailable}
              unavailableHint={t("sku_margin_unavailable_hint")}
            />
          </Flex>
          <Flex gap={"4px"} alignItems={"baseline"}>
            <Text textStyle={"caption-regular"} color={"#8F8F8C"}>
              {tTitle("market_source.poizon")}
            </Text>
            <MarginText margin={estimate.poizon} isUnavailable={false} unavailableHint={""} textStyle={"caption-bold"} />
          </Flex>
        </Flex>
      </Flex>
    </Box>
  );
}

export default function SkuResultSection({ data, onRefresh }: Props) {
  const { t, tTitle } = useI18nHelper();
  const identity = useIdentity();
  const { error: showError, info: showInfo } = useAppToasts();
  const { navigateToBuyerOrderSheetSetPage } = useRouterHelper();
  const { fire: fireCreateOrderSheetSet } = useCreateOrderSheetSet();
  const { fire: fireRefreshKreamSaleHistory } = useRefreshKreamSaleHistory();
  const { fire: fireGetRefreshSaleHistoryStatus } =
    useGetRefreshSaleHistoryStatus();
  const [hoveredProductId, setHoveredProductId] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefreshSaleHistory = async () => {
    if (!data.kreamProductId || isRefreshing) return;
    const kreamProductId = data.kreamProductId;
    setIsRefreshing(true);
    try {
      await fireRefreshKreamSaleHistory({ kreamProductId }, {});

      const POLL_INTERVAL_MS = 2000;
      const MAX_POLL_ATTEMPTS = 60;
      for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
        const res = await fireGetRefreshSaleHistoryStatus(
          { kreamProductId },
          {}
        );
        const status = (res?.status ?? "").toLowerCase();
        if (status === "succeeded") {
          await onRefresh?.();
          return;
        }
        if (status === "failed" || status === "error") {
          showError(new Error("Failed to refresh sale history"));
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, POLL_INTERVAL_MS)
        );
      }
      showError(new Error("Refresh timed out"));
    } catch (e) {
      showError(e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const sortedProducts = useMemo(
    () =>
      [...data.products].sort((a, b) => {
        const aTime = a.lineSheetSet.publishedAt
          ? new Date(a.lineSheetSet.publishedAt).getTime()
          : 0;
        const bTime = b.lineSheetSet.publishedAt
          ? new Date(b.lineSheetSet.publishedAt).getTime()
          : 0;
        return bTime - aTime;
      }),
    [data.products]
  );

  // 마진 기준가 — 주문서 비교 기준(ORDER_SHEET_MARKET_BASIS = bidPrice)과 같다:
  //   크림 = 판매가능 최저가(lowestValue), 포이즌 = 최저 판매가(minPrice).
  // 크림은 체결 2건 미만이면 서버가 marginAvailable=false 로 주고 그때는 마진을 내지 않는다.
  const kreamBasis =
    data.kreamMarket?.marginAvailable !== false
      ? data.kreamMarket?.lowestValue ?? null
      : null;
  const isKreamMarginUnavailable =
    data.kreamMarket != null && data.kreamMarket.marginAvailable === false;
  const poizonBasis = data.poizon?.minPrice ?? null;

  // 예상 매입가는 주문서(오더시트 시세 비교)와 같은 함수로 계산한다 —
  //   (공급가 × (1 + uplift) × 환율) 을 천원 단위로 반올림. uplift·환율은 서버가 준 값.
  const estimates = useMemo(() => {
    const map = new Map<number, ProductEstimate>();
    for (const product of sortedProducts) {
      const breakdown = getEstimatedSupplyBreakdown(
        { amount: product.supplyPrice, currency: product.currency || "EUR" },
        { uplift: product.uplift ?? 0, upliftFactors: product.upliftFactors ?? [] },
        data.pricing
      );
      const value = breakdown?.value;
      map.set(product.id, {
        breakdown,
        kream: value == null ? { expectedMargin: null, marginRate: null } : getPathPickMargin(kreamBasis, value),
        poizon: value == null ? { expectedMargin: null, marginRate: null } : getPathPickMargin(poizonBasis, value),
      });
    }
    return map;
  }, [sortedProducts, data.pricing, kreamBasis, poizonBasis]);

  const hoveredProduct = useMemo(
    () => sortedProducts.find((p) => p.id === hoveredProductId) ?? null,
    [sortedProducts, hoveredProductId]
  );
  // 오른쪽 요약·그래프 기준선은 올려 둔 상품, 없으면 최신 발행 상품
  const focusProduct = hoveredProduct ?? sortedProducts[0] ?? null;
  const focusEstimate = focusProduct ? estimates.get(focusProduct.id) ?? null : null;
  const focusKrw = focusEstimate?.breakdown?.value;

  const currency = sortedProducts[0]?.currency || "EUR";
  const rate = data.pricing?.rates?.[currency];
  const orderRuleCheckCount = sortedProducts.filter(needsOrderRuleCheck).length;

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
    <Grid templateColumns={"minmax(0, 1fr) minmax(0, 1fr)"} gap={"16px"}>
      {/* 왼쪽: 이미지 + 상품 카드 목록 */}
      <Flex flexDirection={"column"} gap={"12px"}>
        {/* 검색된 SKU — 어떤 SKU 의 결과인지 결과 안에서도 보이게, 누르면 복사 */}
        <Flex alignItems={"center"} gap={"6px"} paddingX={"2px"}>
          <Text textStyle={"body-md-bold"} color={"#1A1A1A"}>
            {data.sku}
          </Text>
          <Box
            as={"button"}
            type={"button"}
            aria-label={t("copy")}
            title={t("copy")}
            color={"#8F8F8C"}
            display={"flex"}
            alignItems={"center"}
            padding={"2px"}
            borderRadius={"4px"}
            _hover={{ color: "#1272EF", backgroundColor: "#F2F2EF" }}
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(data.sku);
                showInfo(t("sku_copied"));
              } catch (e) {
                showError(e);
              }
            }}
          >
            <SVG src={"/icons-v1_1/copy.svg"} width={14} height={14} />
          </Box>
        </Flex>
        {/* 이미지 영역: 최대 4개 가로 펼침 */}
        {data.images.length === 0 ? (
          <Box
            borderRadius={"8px"}
            boxShadow={"0 0 0 0.5px #D4D4D1"}
            background={"#FCFCFE"}
            height={"120px"}
            display={"flex"}
            alignItems={"center"}
            justifyContent={"center"}
          >
            <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
              {tTitle("no_image")}
            </Text>
          </Box>
        ) : (
          <Flex gap={"8px"}>
            {data.images.map((src, idx) => (
              <Box
                key={idx}
                width={"120px"}
                height={"120px"}
                flexShrink={0}
                borderRadius={"8px"}
                background={"#FCFCFE"}
              >
                <Image
                  src={src}
                  alt={`${data.sku}-${idx}`}
                  objectFit={"contain"}
                  width={"100%"}
                  height={"100%"}
                />
              </Box>
            ))}
          </Flex>
        )}
        {/* 상품 카드 목록 */}
        {data.products.length > 0 && (
          <DashboardCard>
            <DashboardCard.Header>
              <Flex justifyContent={"space-between"} alignItems={"center"} width={"100%"}>
                <Flex alignItems={"baseline"} gap={"6px"}>
                  <Text textStyle={"body-lg-bold"} color={"#444444"}>
                    {tTitle("available_line_sheets")}
                  </Text>
                  {/* 몇 장에서 살 수 있는지 — 목록이 스크롤 안에 숨어 있어 수를 밖에 보여 준다 */}
                  <Text textStyle={"body-sm-regular"} color={"#8F8F8C"}>
                    {sortedProducts.length}
                  </Text>
                  {orderRuleCheckCount > 0 && (
                    <Text textStyle={"caption-regular"} color={"#C05621"}>
                      · {t("order_rule_check_required")} {orderRuleCheckCount}
                    </Text>
                  )}
                </Flex>
                {/* 환율은 서버가 준 그날의 기준가(주문서와 같은 값) — 손으로 바꾸지 않는다 */}
                <Tooltip label={t("sku_estimate_same_as_order_sheet")} placement={"top"} hasArrow={true} openDelay={150}>
                  <Flex alignItems={"center"} gap={"6px"} cursor={"help"}>
                    <Text textStyle={"body-sm-regular"} color={"#2B6CB0"} fontWeight={"600"} whiteSpace={"nowrap"}>
                      {tTitle("exchange_rate")}
                    </Text>
                    <Text textStyle={"body-sm-regular"} color={"#2B6CB0"} whiteSpace={"nowrap"}>
                      {rate ? `₩${rate.toLocaleString()} / ${currency}` : t("sku_estimate_unavailable")}
                    </Text>
                  </Flex>
                </Tooltip>
              </Flex>
            </DashboardCard.Header>
            <DashboardCard.Body>
              <Flex flexDirection={"column"} gap={"8px"}>
                <Flex gap={"16px"} alignItems={"center"} padding={"0 10px"}>
                  <Flex flexDirection={"column"} flex={1} minW={0} gap={"2px"}>
                    <Text textStyle={"caption-regular"} color={"#8F8F8C"}>공급사</Text>
                    <Text textStyle={"caption-regular"} color={"#8F8F8C"}>{tTitle("sheet")}</Text>
                    <Text textStyle={"caption-regular"} color={"#8F8F8C"}>{tTitle("order_valid_until")}</Text>
                  </Flex>
                  <Flex flexDirection={"column"} width={"72px"} flexShrink={0} alignItems={"flex-end"} gap={"1px"}>
                    <Text textStyle={"caption-regular"} color={"#6B46C1"} fontWeight={"600"}>{tTitle("supply_price")}(€)</Text>
                    <Text textStyle={"caption-regular"} color={"#8F8F8C"}>{tTitle("cost")}(€)</Text>
                    <Text textStyle={"caption-regular"} color={"#8F8F8C"}>{tTitle("retail")}(€)</Text>
                  </Flex>
                  <Text textStyle={"caption-regular"} color={"#8F8F8C"} width={"120px"} flexShrink={0} textAlign={"right"}>
                    {tTitle("estimated_purchase_price")}(₩)
                  </Text>
                  <Text textStyle={"caption-regular"} color={"#8F8F8C"} width={"168px"} flexShrink={0} textAlign={"right"}>
                    {tTitle("estimated_margin")}(₩)
                  </Text>
                </Flex>
                <Flex flexDirection={"column"} gap={"6px"} maxH={"280px"} overflowY={"auto"} padding={"4px"}>
                  {sortedProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      estimate={
                        estimates.get(product.id) ?? {
                          breakdown: null,
                          kream: { expectedMargin: null, marginRate: null },
                          poizon: { expectedMargin: null, marginRate: null },
                        }
                      }
                      isHovered={product.id === hoveredProductId}
                      isKreamMarginUnavailable={isKreamMarginUnavailable}
                      onMouseEnter={() => setHoveredProductId(product.id)}
                      onMouseLeave={() => setHoveredProductId(null)}
                      onClick={() => {
                        if (!identity?.company) return;
                        fireCreateOrderSheetSet(
                          { by: identity.company.type, companyId: identity.company.id },
                          { lineSheetSet: { id: product.lineSheetSet.id } }
                        )
                          .then((ossData) => {
                            navigateToBuyerOrderSheetSetPage(ossData.id, product.lineSheetSet.id, data.sku);
                          })
                          .catch((e) => showError(e));
                      }}
                    />
                  ))}
                </Flex>
              </Flex>
            </DashboardCard.Body>
          </DashboardCard>
        )}
      </Flex>

      {/* 오른쪽: 일별 시장 판매가 그래프 */}
      <DashboardCard>
        <DashboardCard.Header>
          <Flex alignItems={"center"} width={"100%"} minH={"24px"}>
            <Flex flexDirection={"column"} gap={"2px"} minW={0}>
              <Flex alignItems={"center"} gap={"6px"}>
                <Text textStyle={"body-lg-bold"} color={"#444444"}>
                  {tTitle("daily_market_price")}
                </Text>
                {data.kreamProductId !== null && (
                  <Box
                    as={"button"}
                    type={"button"}
                    onClick={handleRefreshSaleHistory}
                    disabled={isRefreshing}
                    aria-label={"refresh sale history"}
                    color={isRefreshing ? "#C5C5C2" : "#8F8F8C"}
                    cursor={isRefreshing ? "not-allowed" : "pointer"}
                    display={"flex"}
                    alignItems={"center"}
                    padding={"2px"}
                    borderRadius={"4px"}
                    _hover={
                      isRefreshing ? undefined : { backgroundColor: "#F2F2EF" }
                    }
                    transition={"background-color 0.15s"}
                  >
                    <Box
                      sx={
                        isRefreshing
                          ? {
                              animation: "spin 0.8s linear infinite",
                              "@keyframes spin": {
                                from: { transform: "rotate(0deg)" },
                                to: { transform: "rotate(360deg)" },
                              },
                            }
                          : undefined
                      }
                      display={"flex"}
                    >
                      <SVG
                        src={"/icons-v1_1/arrow-rotate.svg"}
                        width={14}
                        height={14}
                      />
                    </Box>
                  </Box>
                )}
                {data.saleHistoryUpdatedAt && (
                  <Text textStyle={"caption-regular"} color={"#8F8F8C"}>
                    {formatUpdatedAt(data.saleHistoryUpdatedAt)} {t("updated_at")}
                  </Text>
                )}
              </Flex>
              {focusProduct && (
                <Text textStyle={"body-sm-regular"} color={"#8F8F8C"} noOfLines={1}>
                  #{focusProduct.lineSheetSet.id} {focusProduct.lineSheetSet.boutiqueName}
                  {focusProduct.name ? ` · ${focusProduct.name}` : ""}
                </Text>
              )}
            </Flex>
          </Flex>
        </DashboardCard.Header>
        <DashboardCard.Body>
          {/* 기준가·예상 매입가·예상 마진 — 같은 폭의 칸에 라벨 한 줄·값 한 줄로 맞춘다 */}
          <Grid
            templateColumns={"minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.4fr)"}
            gap={"8px"}
            padding={"0 16px 12px"}
          >
            <Stat
              label={`${tTitle("market_source.kream")} ${t("kream_lowest_ask")}`}
              sub={
                data.kreamMarket?.salesCount != null
                  ? `${tTitle("sales_volume")} ${data.kreamMarket.salesCount.toLocaleString()}`
                  : undefined
              }
            >
              <Text textStyle={"body-md-bold"} color={"#1A1A1A"} whiteSpace={"nowrap"}>
                {data.kreamMarket?.lowestValue != null ? formatKrw(data.kreamMarket.lowestValue) : "-"}
              </Text>
            </Stat>
            <Stat
              label={tTitle("poizon_min_price")}
              sub={
                data.poizon?.sales30d != null
                  ? `${tTitle("poizon_monthly_sales")} ${data.poizon.sales30d.toLocaleString()}`
                  : undefined
              }
            >
              <Text textStyle={"body-md-bold"} color={"#2D9A5F"} whiteSpace={"nowrap"}>
                {data.poizon?.minPrice != null ? formatKrw(data.poizon.minPrice) : "-"}
              </Text>
            </Stat>
            <Stat
              label={tTitle("estimated_purchase_price")}
              sub={focusProduct ? `#${focusProduct.lineSheetSet.id}` : undefined}
            >
              <EstimatedSupplyTooltip breakdown={focusEstimate?.breakdown ?? null}>
                <Text
                  textStyle={"body-md-bold"}
                  color={"#1A1A1A"}
                  whiteSpace={"nowrap"}
                  cursor={focusKrw != null ? "help" : undefined}
                >
                  {focusKrw != null ? formatKrw(focusKrw) : "-"}
                </Text>
              </EstimatedSupplyTooltip>
            </Stat>
            <Stat label={tTitle("estimated_margin")}>
              <Flex flexDirection={"column"} gap={"1px"} minW={0}>
                <Flex gap={"6px"} alignItems={"baseline"}>
                  <Text textStyle={"caption-regular"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
                    {tTitle("market_source.kream")}
                  </Text>
                  <MarginText
                    margin={focusEstimate?.kream ?? { expectedMargin: null, marginRate: null }}
                    isUnavailable={isKreamMarginUnavailable}
                    unavailableHint={t("sku_margin_unavailable_hint")}
                    textStyle={"body-sm-bold"}
                  />
                </Flex>
                <Flex gap={"6px"} alignItems={"baseline"}>
                  <Text textStyle={"caption-regular"} color={"#8F8F8C"} whiteSpace={"nowrap"}>
                    {tTitle("market_source.poizon")}
                  </Text>
                  <MarginText
                    margin={focusEstimate?.poizon ?? { expectedMargin: null, marginRate: null }}
                    isUnavailable={false}
                    unavailableHint={""}
                    textStyle={"body-sm-bold"}
                  />
                </Flex>
              </Flex>
            </Stat>
          </Grid>
          <Box height={"320px"} paddingBottom={"16px"}>
            <SkuPriceChart
              data={data.saleHistory}
              poizonData={data.poizon?.priceHistory ?? []}
              supplyPriceKrw={focusKrw}
              supplyPriceLabel={
                focusProduct
                  ? `#${focusProduct.lineSheetSet.id} ${tTitle("estimated_purchase_price")}`
                  : undefined
              }
            />
          </Box>
        </DashboardCard.Body>
      </DashboardCard>
    </Grid>
    </Flex>
  );
}
